import { useMemo } from 'react';
import { propagate } from 'satellite.js';
import { useLoader } from '../store';
import { useSSA } from './store';
import { useMonitoring } from './monitoringStore';
import { PROXIMITY_COLOURS, proximityBand, forecastCurrent } from './monitoring';
import { parseOrbitalInput } from './orbit';
import { relativeGeometry } from './geometry';
import { rangeText, rateText } from './quality';

export function MonitoringPanel({ selectedId }: { selectedId: string }) {
  const dataset = useLoader((s) => s.dataset),
    result = useSSA((s) => s.result),
    enabled = useSSA((s) => s.predictEncounters),
    zones = useSSA((s) => s.zones);
  const radius = useSSA((s) => s.radiusKm);
  const { forecast, status, message, revision, notices } = useMonitoring();
  const byId = useMemo(() => new Map(dataset?.missions.map((m) => [m.id, m]) ?? []), [dataset]);
  const at = result?.timestamp ?? 0;
  const valid = forecastCurrent(forecast, selectedId, revision, dataset?.catalogueSnapshot?.revision, at);
  const upcoming = useMemo(
    () =>
      valid && forecast
        ? forecast.encounters
            .filter((e) => e.closest.t > at && e.closest.rangeKm <= zones.close)
            .sort((a, b) => a.closest.rangeKm - b.closest.rangeKm)
        : [],
    [valid, forecast, at, zones.close],
  );
  const current = useMemo(() => {
    const rows = new Map(result?.neighbours.map((n) => [n.id, n]) ?? []);
    const source = byId.get(selectedId)?.trackedObject;
    const rec = source && parseOrbitalInput({ id: selectedId, omm: source.omm }),
      a = rec && propagate(rec, new Date(at));
    if (a)
      for (const e of upcoming.slice(0, 3))
        if (!rows.has(e.id)) {
          const object = byId.get(e.id)?.trackedObject,
            br = object && parseOrbitalInput({ id: e.id, omm: object.omm }),
            b = br && propagate(br, new Date(at));
          if (b) rows.set(e.id, { id: e.id, ...relativeGeometry(a, b), positionEcfKm: { x: 0, y: 0, z: 0 } });
        }
    return rows;
  }, [result, byId, selectedId, at, upcoming]);
  return (
    <div className="rounded border border-cyan-200/15 bg-white/[.02] px-2.5 py-2 text-[10px]">
      <div className="flex items-center gap-2">
        <span className="font-medium text-slate-300">
          {enabled ? 'Predicted encounters' : 'Prediction off'}
        </span>
        <span className="ml-auto text-slate-400">
          {enabled
            ? status === 'scanning'
              ? 'Scanning…'
              : status === 'error'
                ? valid ? 'Previous scan' : 'Incomplete'
              : valid
                ? upcoming.length + ' upcoming'
                : 'Not current'
            : ''}
        </span>
      </div>
      {enabled &&
        upcoming.slice(0, 1).map((e) => {
          const row = current.get(e.id),
            color = PROXIMITY_COLOURS[proximityBand(e.closest.rangeKm, zones)];
          return (
            <button
              key={e.id}
              className="mt-2 block w-full border-l-2 pl-2 text-left hover:bg-white/5"
              style={{ borderColor: color }}
              onClick={() => useSSA.setState({ pinnedId: e.id, analysis: null })}
              title="Pin this object without changing the clock"
            >
              <span className="block truncate text-slate-200">{byId.get(e.id)?.name ?? e.id}</span>
              <span className="block" style={{ color }}>
                Predicted {rangeText(e.closest.rangeKm)} · in{' '}
                {e.closest.t - at < 90000
                  ? Math.max(1, Math.round((e.closest.t - at) / 1000)) + ' s'
                  : Math.ceil((e.closest.t - at) / 60000) + ' min'}
              </span>
              <span className="text-slate-400">
                {row
                  ? rangeText(row.rangeKm) + ' now · ' + rateText(row.rangeRateKmS)
                  : 'Current range unavailable'}
              </span>
            </button>
          );
        })}
      <details className="mt-1.5 text-slate-400">
        <summary className="cursor-pointer">
          Coverage & behaviour watch{notices.length ? ' · ' + notices.length + ' flags' : ''}
        </summary>
        {enabled && (
          <>
            <p className="mt-2">
              Ten-minute scans across all catalogue types, beyond the display radius. Geometric predictions,
              not measured links or collision probability.
            </p>
            <p>
              {valid
                ? forecast.searched.toLocaleString() +
                  ' searched · ' +
                  forecast.unavailable +
                  ' partly or wholly unavailable. Coverage ends ' +
                  new Date(forecast.endMs).toISOString().slice(11, 19) +
                  'Z.'
                : 'No current forecast coverage.'}
            </p>
            {message && <p className="mt-1 text-amber-200">{message}</p>}
            <button
              className="my-2 rounded border border-cyan-200/20 px-2 py-1 text-slate-200 disabled:opacity-40"
              disabled={status === 'scanning'}
              onClick={() => useMonitoring.setState((s) => ({ rescan: s.rescan + 1 }))}
            >
              Scan again
            </button>
          </>
        )}
        <p>
          Behaviour watch follows up to 512 nearest objects in the selected search scope. It describes
          modelled motion in this continuous session, not surveillance intent. Gaps and time jumps restart
          dwell tracking.
        </p>
        {radius < zones.close * 1.03 && (
          <p className="mt-1 text-amber-200">
            Increase the search radius beyond {Math.ceil(zones.close * 1.03)} km to enable behaviour watch.
          </p>
        )}
        {upcoming.length > 1 && (
          <div className="my-2 max-h-32 overflow-y-auto" aria-label="Additional predicted encounters">
            {upcoming.slice(1, 11).map((e) => (
              <button
                key={e.id}
                className="flex w-full justify-between gap-2 border-t border-white/10 py-1 text-left"
                onClick={() => useSSA.setState({ pinnedId: e.id, analysis: null })}
              >
                <span className="truncate">{byId.get(e.id)?.name ?? e.id}</span>
                <span className="shrink-0">
                  {rangeText(e.closest.rangeKm)} · {new Date(e.closest.t).toISOString().slice(11, 19)}Z
                </span>
              </button>
            ))}
          </div>
        )}
        {notices.length === 0 && <p className="mt-1">No behaviour flags accumulated in this session.</p>}
        {notices.slice(0, 10).map((n, i) => (
          <button
            key={n.id + ':' + n.kind + ':' + i}
            className="mt-2 block w-full border-t border-white/10 pt-2 text-left"
            onClick={() => useSSA.setState({ pinnedId: n.id, analysis: null })}
          >
            <span className="text-amber-100">{n.kind}</span>
            <span className="block text-slate-300">{byId.get(n.id)?.name ?? n.id}</span>
            {n.detail}
            <span className="block">{new Date(n.at).toISOString().slice(11, 19)}Z</span>
          </button>
        ))}
      </details>
    </div>
  );
}
